import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  InsertionPosition,
  WidgetAutocheck,
} from "@redotech/redo-model/widget";
import { ShopifyCart } from "@redotech/shopify-client/ajax";
import debounce from "lodash/debounce";
import { createContext, useEffect, useState } from "react";
import { useCart } from "../../utils/cart";
import { TryFirstContext } from "../context";
import DueLater from "./due-later";
import { CART_TOTAL_SELECTOR } from "./selectors";
import { TryFirstCartModification } from "./try-first-cart-modification-container";

const formatCurrency = (amount: number, currency: string): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

interface TryFirstModificationsContextType {
  cart: ShopifyCart | undefined;
  tryFirstTotal: number;
  dueTodayTotal: number;
  currency: string;
  formatCurrency: (amount: number, currency: string) => string;
}

const DEBOUNCE_CHANGES_MS = 100; // If the DOM is changing more often than 100ms, wait until it slows down
const MAX_DEBOUNCE_MS = 300; // Never wait longer than 1000ms to try to re-add ourselves

export const useShouldRefreshCartModifications = () => {
  const [refreshCartModifications, setRefreshCartModifications] =
    useState<symbol>(Symbol());
  const domStateStale = useHandler(
    debounce(
      () => {
        setRefreshCartModifications(Symbol());
      },
      DEBOUNCE_CHANGES_MS,
      { maxWait: MAX_DEBOUNCE_MS, leading: false, trailing: true },
    ),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      domStateStale();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [domStateStale]);

  return refreshCartModifications;
};

export const TryFirstModificationsContext = createContext<
  TryFirstModificationsContextType | undefined
>(undefined);

export const getTryFirstTotals = ({
  cart,
  autocheck,
}: {
  cart: ShopifyCart;
  autocheck: WidgetAutocheck;
}): { dueTodayTotal: number; dueLaterTotal: number } => {
  const cartTotal = (Number(cart.total_price) || 0) * 100;

  if (!autocheck.tryFirst?.sellingPlanId) {
    return { dueTodayTotal: cartTotal, dueLaterTotal: 0 };
  }

  const tryFirstTotal = cart.items
    .filter((item) => {
      const lineItemSellingPlanIdStr =
        item.selling_plan?.toString() ??
        item.selling_plan_allocation?.selling_plan?.id.toString();
      return (
        lineItemSellingPlanIdStr ===
        autocheck.tryFirst?.sellingPlanId?.toString()
      );
    })
    .reduce((total, item) => total + (item.line_price || 0), 0);

  const dueTodayTotal = cartTotal - tryFirstTotal;

  return { dueTodayTotal, dueLaterTotal: tryFirstTotal };
};

const TryFirstCartModifications = () => {
  const cart = useCart();
  const tryFirstContext = useRequiredContext(TryFirstContext);
  const [dueNowAmount, setDueNowAmount] = useState<number | null>(null);
  const [dueLaterAmount, setDueLaterAmount] = useState<number | null>(null);

  useEffect(() => {
    if (
      !cart ||
      !cart.items.length ||
      !tryFirstContext.autocheck.tryFirst?.sellingPlanId
    ) {
      return;
    }

    const { dueTodayTotal, dueLaterTotal } = getTryFirstTotals({
      cart,
      autocheck: tryFirstContext.autocheck,
    });

    setDueNowAmount(dueTodayTotal);
    setDueLaterAmount(dueLaterTotal);
  }, [cart, tryFirstContext.autocheck]);

  const contextValue: TryFirstModificationsContextType = {
    cart,
    tryFirstTotal: dueLaterAmount ?? 0,
    dueTodayTotal: dueNowAmount ?? 0,
    currency: cart?.currency ?? "USD",
    formatCurrency,
  };

  return (
    <TryFirstModificationsContext.Provider value={contextValue}>
      <TryFirstCartModification
        containerPlacement={InsertionPosition.AFTER}
        containerSelector={CART_TOTAL_SELECTOR}
      >
        <DueLater />
      </TryFirstCartModification>
    </TryFirstModificationsContext.Provider>
  );
};

export default TryFirstCartModifications;
